import { useMemo} from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';

export const RTFOMonitorChart = ({ gridView, showSpikeLines, showLegend }) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const stageData = currentStage?.coldboreData;
  const monitorData = currentStage?.monitorData;
  const stageMetrics = currentStage?.metrics;

  const darkMode = user?.value?.darkMode;
  
  const data = useMemo(() => {
    if ((!stageData || stageData?.length === 0)) {
      return [];
    };

    // // Get the timezone offset in minutes and convert to milliseconds
    // const timezoneOffset = new Date().getTimezoneOffset() * 60000;

    const client = currentRTFO?.client;

    return getChartData();

    function getChartData() {
      try {
  
        const X = [];
        const YPressure = [];
  
        stageData?.forEach((d, i) => {
          X.push(new Date(d.timestamp));
          YPressure.push(d.pressure > 0 ? d.pressure : null);
        });
  
        let traces = [
          { // treating pressure 
            x: X,
            y: YPressure,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y1',
            name: 'Treating Pr. (MPa)',
            line: {
              width: 2,
              color: chartColors[2]
            },
            hoverinfo: !showSpikeLines && 'none',
            connectgaps: true
          }
        ];
          
        monitorData && Object.keys(monitorData).length > 0 && Object.keys(monitorData).forEach((monitorJobId, i) => {
          const XMonitor = [];
          const YCasingPressure = [];
          // const YTubingPressure = []; // temporarily disable tubing pressure
          const YPressurePrime = [];
          // const YPressurePrimePrime = [];

          // Get the timezone offset in minutes and convert to milliseconds
          const timezoneOffset = new Date(monitorData[monitorJobId]?.[0]?.timestamp).getTimezoneOffset() * 60000;
          
          monitorData[monitorJobId]?.forEach(d => {
            XMonitor.push(new Date(d.timestamp + ((client === 'yoho' || currentRTFO?.name === 'Test-2') ? 0 : timezoneOffset)));
            YCasingPressure.push(d.casingPressure);
            // YTubingPressure.push(d.tubingPressure);
            YPressurePrime.push(d.pressurePrime || d.casingPressurePrime);
            // YPressurePrimePrime.push(d.pressurePrimePrime);
          });
  
          traces = traces.concat([
            { // casing pressure
              x: XMonitor,
              y: YCasingPressure?.length > 0 ? YCasingPressure : [0],
              mode: 'line',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y2',
              name: `${monitorJobId}-Casing Pr. (MPa)`,
              line: {
                width: 2,
                color: chartColors[1]
              },
              marker: {
                size: 0,
                color: 'transparent'
              },
              hoverinfo: !showSpikeLines && 'none',
              legendgroup: monitorJobId
            },
            // { // tubing pressure
            //   x: XMonitor,
            //   y: YTubingPressure?.length > 0 ? YTubingPressure : [0],
            //   mode: 'line',
            //   type: 'scattergl',
            //   xaxis: 'x1',
            //   yaxis: 'y2',
            //   name: `${monitorJobId}-Tubing Pr. (MPa)`,
            //   line: {
            //     width: 2,
            //     color: chartColors[3]
            //   },
            //   marker: {
            //     size: 0,
            //     color: 'transparent'
            //   },
            //   hoverinfo: !showSpikeLines && 'none',
            //   legendgroup: monitorJobId,
            //   showlegend: false
            // },
            {
              x: XMonitor,
              y: YPressurePrime?.length > 0 ? YPressurePrime : [0],
              mode: 'line',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y3',
              name: `${monitorJobId}-Casing Pr.'`,
              line: {
                width: 2,
                color: chartColors[7],
                dash: 'dot'
              },
              marker: {
                size: 0,
                color: 'transparent'
              },
              hoverinfo: !showSpikeLines && 'none',
              legendgroup: monitorJobId,
              showlegend: false
            },
            // {
            //   x: XMonitor,
            //   y: YPressurePrimePrime?.length > 0 ? YPressurePrimePrime : [0],
            //   mode: 'line',
            //   type: 'scattergl',
            //   xaxis: 'x1',
            //   yaxis: 'y3',
            //   name: `${monitorJobId}-Casing Pr.''`,
            //   line: {
            //     width: 2,
            //     color: chartColors[5],
            //     dash: 'dashdot'
            //   },
            //   hoverinfo: !showSpikeLines && 'none'
            // }
          ]);
        });
  
        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [stageData, currentRTFO?.client, currentRTFO?.name, showSpikeLines, monitorData]);


  

  const layout = {
    // grid: {rows: 3, columns: 2, pattern: 'independent'},
    yaxis: { // Treating pressure y axis
      linecolor: chartColors[2],
      showgrid: false,
      color: chartColors[2],
      title: {
        text: 'Treating Pressure (MPa)',
        font: {
          size: 12
        },
      },
      range: [0, 100],
      position: 0,
      anchor: 'x',
      side: 'left',
    },
    yaxis2: { // Monitor pressure y axis
      linecolor: chartColors[1],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[1],
      title: {
        text: 'Monitor Pressure (MPa)',
        font: {
          size: 12
        },
      },
      // range: [0, 50],
      autorange: true,
      anchor: 'free',
      overlaying: 'y',
      side: 'left',
      position: 0
    },
    
    yaxis3: { // Pressure derivative y axis
      linecolor: chartColors[7],
      showgrid: false,
      color: chartColors[7],
      title: {
        text: 'Pressure Derivative (MPa/min)',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: [-4, 4],
      side: 'right',
      overlaying: 'y'
    },
    xaxis: { // Date x axis
      type: 'date',
      autorange: true,
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      domain: [(gridView ? 0.1 : 0.05), 1],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across"
    },
    hovermode: "x",
    hoverdistance: 10, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: showLegend ? true : false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.2 : -0.1),
    },
    annotations: [
      {
        text: "Monitor Wells",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.09 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      stageMetrics?.preSandTimestamp && { // pre sand line annotation
        x: new Date(stageMetrics?.preSandTimestamp),
        y: 1,
        xref: 'x1',
        yref: 'paper',
        text: 'Pre Sand',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      },
      stageMetrics?.breakdownTimestamp && { // breakdown line annotation
        x: new Date(stageMetrics?.breakdownTimestamp),
        y: 1,
        xref: 'x1',
        yref: 'paper',
        text: 'Breakdown',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      }
    ],
    shapes: [
      stageMetrics?.preSandTimestamp && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(stageMetrics?.preSandTimestamp),
        y0: 0,
        x1: new Date(stageMetrics?.preSandTimestamp),
        y1: 0.99,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 2,
          dash: 'dot'
        }
      },
      stageMetrics?.breakdownTimestamp && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(stageMetrics?.breakdownTimestamp),
        y0: 0,
        x1: new Date(stageMetrics?.breakdownTimestamp),
        y1: 0.99,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 2,
          dash: 'dot'
        }
      }
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (gridView ? 0.05 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };


  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );

  
};
